// src/modules/share-card.ts
import QRCode from 'qrcode-svg';
import { DIMS } from '../constants/dimensions.js';
import type { ScoreResult, ProfileEntry } from '../types/index.js';

interface ShareCardOpts {
  result: ScoreResult;
  profile: Partial<ProfileEntry> | null;
  strategies: string[];
  siteUrl: string;
  qrImage: HTMLImageElement | null;
}

interface Orientation {
  w: number;
  h: number;
}

const SIZES: Record<'portrait' | 'landscape', Orientation> = {
  portrait: { w: 1080, h: 1920 },
  landscape: { w: 1200, h: 630 },
};

// 中文字体回退链 - 解决 macOS / Win / Linux 字体差异
const ZH_FONT = '-apple-system, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "WenQuanYi Micro Hei", sans-serif';

const COLORS = {
  bgFrom: '#fef6ec',
  bgTo: '#f8e8d4',
  text: '#3d2e1a',
  soft: '#8a6f4d',
  accent: '#d4925a',
  accentDark: '#b87642',
  cardBg: 'rgba(255,255,255,0.45)',
  divider: 'rgba(212,146,90,0.35)',
};

function font(size: number, weight: string = '400'): string {
  return `${weight} ${size}px ${ZH_FONT}`;
}

// 站点固定域名: 显示文本与 QR 内容
const SITE_DISPLAY = 'vark.bind.pub';
const SITE_FULL = 'https://vark.bind.pub';

/**
 * 解析类型 label,从 "K · 实操派学习者 · 单模态" 中拆出 主标题 / 模态标签
 */
function parseLabel(label: string | undefined, code: string): { title: string; modality: string } {
  if (!label) return { title: code, modality: '' };
  const parts = String(label).split(/\s*[·•・]\s*/);
  // 去掉首部的 code
  const stripped = parts[0] && parts[0].trim() === code ? parts.slice(1) : parts;
  const title = stripped[0] || code;
  const modality = stripped[1] || '';
  return { title, modality };
}

/**
 * 加载图片 (用于 QR 码异步绘制)
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * 生成 QR 码 dataURL
 */
function makeQrDataUrl(text: string, size: number = 120): string | null {
  try {
    const svg = new QRCode({
      content: text,
      padding: 1,
      width: size,
      height: size,
      color: '#3d2e1a',
      background: '#fef6ec',
      ecl: 'M',
      join: true,
    }).svg();
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  } catch {
    return null;
  }
}

/**
 * 圆角矩形路径
 */
function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

/**
 * 绘制 portrait 1080×1920 卡片
 */
function drawPortrait(
  ctx: CanvasRenderingContext2D, w: number, h: number, opts: ShareCardOpts,
): void {
  const { result, profile, strategies, siteUrl, qrImage } = opts;
  const code = result.code;
  const tally = result.tally;
  const preferred = new Set(result.preferred || []);
  const max = Math.max(...DIMS.map((d) => tally[d]), 16);
  const { title, modality } = parseLabel(profile?.label, code);
  const tagline = profile?.tagline || '';

  // === 背景渐变 ===
  const grad = ctx.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, COLORS.bgFrom);
  grad.addColorStop(1, COLORS.bgTo);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // 装饰边框
  ctx.strokeStyle = COLORS.divider;
  ctx.lineWidth = 2;
  roundRectPath(ctx, 40, 40, w - 80, h - 80, 32);
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // === 顶部副标题 ===
  ctx.fillStyle = COLORS.soft;
  ctx.font = font(28);
  ctx.fillText('VARK 学习风格测试', w / 2, 110);

  // === 类型代码大字 (220px,占据 ~y=210..430) ===
  ctx.fillStyle = COLORS.accent;
  ctx.font = font(220, '900');
  ctx.fillText(code, w / 2, 320);

  // === 类型标题 ===
  ctx.fillStyle = COLORS.text;
  ctx.font = font(54, 'bold');
  ctx.fillText(title, w / 2, 580);

  // === 模态副标 ===
  if (modality) {
    ctx.fillStyle = COLORS.soft;
    ctx.font = font(30);
    ctx.fillText(`· ${modality} ·`, w / 2, 655);
  }

  // === Tagline (字号缩到 32) ===
  if (tagline) {
    ctx.fillStyle = COLORS.accentDark;
    ctx.font = font(32, '500');
    ctx.fillText(`“${tagline}”`, w / 2, 735);
  }

  // === 分割线 ===
  ctx.strokeStyle = COLORS.divider;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(w / 2 - 80, 775);
  ctx.lineTo(w / 2 + 80, 775);
  ctx.stroke();

  // === 雷达图 (缩半径,中心下移以避让上方 tagline) ===
  const cx = w / 2;
  const cy = 1060;
  const radius = 200;

  // 刻度环 4 圈
  ctx.strokeStyle = 'rgba(212,146,90,0.25)';
  ctx.lineWidth = 1.5;
  for (let r = 1; r <= 4; r++) {
    const rr = (radius * r) / 4;
    ctx.beginPath();
    DIMS.forEach((_, i) => {
      const a = -Math.PI / 2 + (i * Math.PI) / 2;
      const x = cx + Math.cos(a) * rr;
      const y = cy + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.stroke();
  }

  // 4 条对角轴
  ctx.strokeStyle = 'rgba(212,146,90,0.3)';
  DIMS.forEach((_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
    ctx.stroke();
  });

  // 数据多边形
  ctx.fillStyle = 'rgba(212,146,90,0.3)';
  ctx.strokeStyle = COLORS.accentDark;
  ctx.lineWidth = 4;
  ctx.beginPath();
  DIMS.forEach((d, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 2;
    const r = (tally[d] / max) * radius;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // 数据点
  DIMS.forEach((d, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 2;
    const r = (tally[d] / max) * radius;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    ctx.fillStyle = preferred.has(d) ? COLORS.accentDark : COLORS.accent;
    ctx.beginPath();
    ctx.arc(x, y, 8, 0, Math.PI * 2);
    ctx.fill();
  });

  // 轴标签 (维度名 + 分数)
  ctx.fillStyle = COLORS.text;
  ctx.font = font(34, 'bold');
  DIMS.forEach((d, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 2;
    const x = cx + Math.cos(a) * (radius + 52);
    const y = cy + Math.sin(a) * (radius + 52);
    ctx.fillText(`${d} ${tally[d]}`, x, y);
  });

  // === 维度分数胶囊 2x2 ===
  const capW = 220;
  const capH = 64;
  const gap = 28;
  const gridW = capW * 2 + gap;
  const gridX = (w - gridW) / 2;
  const gridY = 1370;
  DIMS.forEach((d, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = gridX + col * (capW + gap);
    const y = gridY + row * (capH + 18);
    const isPref = preferred.has(d);
    if (isPref) {
      ctx.fillStyle = COLORS.accent;
      roundRectPath(ctx, x, y, capW, capH, 32);
      ctx.fill();
      ctx.fillStyle = '#fff';
    } else {
      ctx.strokeStyle = COLORS.divider;
      ctx.lineWidth = 2;
      roundRectPath(ctx, x, y, capW, capH, 32);
      ctx.stroke();
      ctx.fillStyle = COLORS.soft;
    }
    ctx.font = font(30, 'bold');
    ctx.fillText(`${d}  ${tally[d]}`, x + capW / 2, y + capH / 2);
  });

  // 总分 / step (胶囊底部 1370+64*2+18=1516,给 40px 间隔 → y=1556)
  ctx.fillStyle = COLORS.soft;
  ctx.font = font(24);
  ctx.fillText(
    `总分 ${result.total ?? DIMS.reduce((s, d) => s + tally[d], 0)} · step ${result.step ?? '-'}`,
    w / 2,
    1556,
  );

  // === 学习策略分割线 ===
  ctx.strokeStyle = COLORS.divider;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(w / 2 - 120, 1605);
  ctx.lineTo(w / 2 + 120, 1605);
  ctx.stroke();

  // === 学习策略标题 ===
  ctx.textAlign = 'center';
  ctx.fillStyle = COLORS.accentDark;
  ctx.font = font(32, 'bold');
  ctx.fillText('💡 给你的 3 条学习策略', w / 2, 1655);

  // === 学习策略 3 条 (各占 40px 间隔,避免与右下 QR 重叠) ===
  ctx.textAlign = 'left';
  ctx.fillStyle = COLORS.text;
  ctx.font = font(26);
  const stratList = (strategies || []).slice(0, 3);
  const stratX = 100;
  // QR 在右下 x=860..1000,y=1700..1840 区域,因此策略文字起 y=1715,逐行 +40
  stratList.forEach((s, i) => {
    const text = `✓ ${s}`;
    // 收紧截断防止与 QR 区水平重叠 (右边界 ~840px,字号 26px → 约 27 字)
    const truncated = text.length > 26 ? text.slice(0, 25) + '…' : text;
    ctx.fillText(truncated, stratX, 1715 + i * 42);
  });

  // === 底部站点信息 ===
  ctx.textAlign = 'center';
  ctx.fillStyle = COLORS.soft;
  ctx.font = font(26, '500');
  ctx.fillText(siteUrl, w / 2, 1860);
  ctx.font = font(22);
  ctx.fillText('扫码或访问开始你的测试', w / 2, 1895);

  // QR 码绘制 (右下角,固定 right=80, bottom=80, size=140)
  if (qrImage) {
    const qrSize = 140;
    const qrX = w - qrSize - 80;
    const qrY = h - qrSize - 80;
    ctx.fillStyle = '#fff';
    roundRectPath(ctx, qrX, qrY, qrSize, qrSize, 12);
    ctx.fill();
    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
  }
}

/**
 * 绘制 landscape 1200×630 卡片
 */
function drawLandscape(
  ctx: CanvasRenderingContext2D, w: number, h: number, opts: ShareCardOpts,
): void {
  const { result, profile, siteUrl, qrImage } = opts;
  const code = result.code;
  const tally = result.tally;
  const preferred = new Set(result.preferred || []);
  const max = Math.max(...DIMS.map((d) => tally[d]), 16);
  const { title, modality } = parseLabel(profile?.label, code);
  const tagline = profile?.tagline || '';

  // 背景
  const grad = ctx.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, COLORS.bgFrom);
  grad.addColorStop(1, COLORS.bgTo);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  ctx.strokeStyle = COLORS.divider;
  ctx.lineWidth = 2;
  roundRectPath(ctx, 24, 24, w - 48, h - 48, 24);
  ctx.stroke();

  ctx.textBaseline = 'middle';

  // 左侧
  ctx.textAlign = 'center';
  const leftCx = w * 0.28;

  ctx.fillStyle = COLORS.soft;
  ctx.font = font(22);
  ctx.fillText('VARK 学习风格测试', leftCx, 60);

  ctx.fillStyle = COLORS.accent;
  ctx.font = font(150, '900');
  ctx.fillText(code, leftCx, 180);

  ctx.fillStyle = COLORS.text;
  ctx.font = font(38, 'bold');
  ctx.fillText(title, leftCx, 320);

  if (modality) {
    ctx.fillStyle = COLORS.soft;
    ctx.font = font(22);
    ctx.fillText(`· ${modality} ·`, leftCx, 365);
  }

  if (tagline) {
    ctx.fillStyle = COLORS.accentDark;
    ctx.font = font(24, '500');
    ctx.fillText(`“${tagline}”`, leftCx, 420);
  }

  // 右侧雷达图 (中心 240,半径 160,不与底部胶囊/URL 重叠)
  const cx = w * 0.72;
  const cy = 240;
  const radius = 160;
  ctx.strokeStyle = 'rgba(212,146,90,0.25)';
  ctx.lineWidth = 1.5;
  for (let r = 1; r <= 4; r++) {
    const rr = (radius * r) / 4;
    ctx.beginPath();
    DIMS.forEach((_, i) => {
      const a = -Math.PI / 2 + (i * Math.PI) / 2;
      const x = cx + Math.cos(a) * rr;
      const y = cy + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.stroke();
  }
  DIMS.forEach((_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
    ctx.stroke();
  });

  ctx.fillStyle = 'rgba(212,146,90,0.3)';
  ctx.strokeStyle = COLORS.accentDark;
  ctx.lineWidth = 3;
  ctx.beginPath();
  DIMS.forEach((d, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 2;
    const r = (tally[d] / max) * radius;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = COLORS.text;
  ctx.font = font(22, 'bold');
  DIMS.forEach((d, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 2;
    const x = cx + Math.cos(a) * (radius + 34);
    const y = cy + Math.sin(a) * (radius + 34);
    ctx.fillText(`${d} ${tally[d]}`, x, y);
  });

  // 4 个胶囊 (全画布水平居中,避开右下 QR)
  const capW = 110;
  const capH = 36;
  const gap = 14;
  const totalW = capW * 4 + gap * 3;
  const startX = (w - totalW) / 2;
  const capY = 510;
  DIMS.forEach((d, i) => {
    const x = startX + i * (capW + gap);
    const isPref = preferred.has(d);
    if (isPref) {
      ctx.fillStyle = COLORS.accent;
      roundRectPath(ctx, x, capY, capW, capH, 18);
      ctx.fill();
      ctx.fillStyle = '#fff';
    } else {
      ctx.strokeStyle = COLORS.divider;
      ctx.lineWidth = 2;
      roundRectPath(ctx, x, capY, capW, capH, 18);
      ctx.stroke();
      ctx.fillStyle = COLORS.soft;
    }
    ctx.font = font(20, 'bold');
    ctx.textAlign = 'center';
    ctx.fillText(`${d}  ${tally[d]}`, x + capW / 2, capY + capH / 2);
  });

  // 底部 URL
  ctx.fillStyle = COLORS.soft;
  ctx.font = font(20, '500');
  ctx.textAlign = 'center';
  ctx.fillText(siteUrl, w / 2, 590);

  if (qrImage) {
    const qrSize = 80;
    const qrX = w - qrSize - 30;
    const qrY = h - qrSize - 30;
    ctx.fillStyle = '#fff';
    roundRectPath(ctx, qrX, qrY, qrSize, qrSize, 8);
    ctx.fill();
    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
  }
}

/**
 * 生成分享卡片 dataURL
 *
 * @param {object} args
 * @param {object} args.result 计分结果 {code, tally, preferred, total, step}
 * @param {object} [args.profile] {label, tagline, description, careers}
 * @param {string[]} [args.strategies] 学习策略文字数组,取前 3 条
 * @param {string} args.siteUrl 站点 URL,用于显示与 QR 码
 * @param {'portrait'|'landscape'} [args.orientation='portrait']
 * @returns {Promise<string>} dataURL
 */
export interface GenerateShareCardArgs {
  result: ScoreResult;
  profile?: Partial<ProfileEntry> | null;
  strategies?: string[];
  siteUrl?: string;
  orientation?: 'portrait' | 'landscape';
  /** 兼容旧调用 */
  label?: string;
}

export async function generateShareCard({
  result,
  profile,
  strategies,
  siteUrl,
  orientation = 'portrait',
  // 兼容旧调用 (label 仍可传入)
  label,
}: GenerateShareCardArgs): Promise<string> {
  // 兼容只传 label 的旧用法
  const safeProfile: Partial<ProfileEntry> | null = profile || (label ? { label } : null);
  // 显示文本固定为 vark.bind.pub;QR 内容固定为 https://vark.bind.pub
  // siteUrl 参数保留兼容,但仅作为显示文本的可选覆盖
  const displayUrl = normalizeSiteUrl(siteUrl);
  const qrTarget = SITE_FULL;

  const { w, h } = SIZES[orientation] || SIZES.portrait;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('failed to acquire 2d context');

  // 异步加载 QR 码
  let qrImage: HTMLImageElement | null = null;
  const qrDataUrl = makeQrDataUrl(qrTarget, orientation === 'portrait' ? 140 : 80);
  if (qrDataUrl && typeof Image !== 'undefined') {
    try {
      qrImage = await loadImage(qrDataUrl);
    } catch {
      qrImage = null;
    }
  }

  const opts: ShareCardOpts = {
    result,
    profile: safeProfile,
    strategies: strategies || [],
    siteUrl: displayUrl,
    qrImage,
  };
  if (orientation === 'landscape') {
    drawLandscape(ctx, w, h, opts);
  } else {
    drawPortrait(ctx, w, h, opts);
  }
  return canvas.toDataURL('image/png');
}

/**
 * 站点显示文本归一化:统一固定为 vark.bind.pub。
 * siteUrl 参数仅作兼容保留——若未传或传入 localhost/IP 等本地地址,
 * 都回落到固定的 vark.bind.pub。
 */
function normalizeSiteUrl(siteUrl: string | undefined): string {
  if (!siteUrl) return SITE_DISPLAY;
  if (/localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(siteUrl)) return SITE_DISPLAY;
  // 若调用方传了正式 URL,优先剥离 scheme 让显示更简洁,失败则用回 SITE_DISPLAY
  try {
    const u = new URL(siteUrl);
    return u.host || SITE_DISPLAY;
  } catch {
    return SITE_DISPLAY;
  }
}

export function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
}
